import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Container,
  Button,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Token as TokenIcon,
  Assessment as AssessmentIcon,
  ExitToApp as LogoutIcon,
  MenuBook as DocsIcon,
  Home as HomeIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { checkSession, logout } from '../../services/authService';
import Overview from './overView';
import TokenInfo from './tokenInfo';
import UsageLog from './usageLog';
import ApiStatus from './apiStatus';

const ConsolePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState<string>(
    searchParams.get('tab') || 'token-info'
  );
  const [loading, setLoading] = useState<boolean>(true);
  const navigate = useNavigate();
  const theme = useTheme();

  const menuItems = [
    { id: 'token-info', label: '토큰 정보', icon: <TokenIcon /> },
    { id: 'usage-log', label: '사용 기록', icon: <AssessmentIcon /> },
    { id: 'api-status', label: 'API 상태', icon: <SpeedIcon /> },
  ];

  const handlePageChange = (pageId: string) => {
    setCurrentPage(pageId);
    setSearchParams({ tab: pageId });
  };

  useEffect(() => {
    const verifySession = async () => {
      const session = await checkSession();
      if (!session) {
        navigate('/login');
      } else {
        setLoading(false);
      }
    };
    verifySession();
  }, [navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const renderPageContent = () => {
    switch (currentPage) {
      case 'token-info':
        return <TokenInfo />;
      case 'usage-log':
        return <UsageLog />;
      case 'api-status':
        return <ApiStatus />;
      default:
        return <TokenInfo />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6" color="textSecondary">Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'grey.50' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: 280,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
            bgcolor: 'white',
            borderRight: '1px solid',
            borderColor: 'grey.200',
          },
        }}
      >
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'grey.200' }}>
          <Typography variant="h6" color="primary.main" fontWeight="bold">
            Open API Console
          </Typography>
        </Box>
        
        <List sx={{ px: 2, py: 3 }}>
          {menuItems.map((item) => (
            <ListItemButton
              key={item.id}
              onClick={() => handlePageChange(item.id)}
              selected={currentPage === item.id}
              sx={{
                borderRadius: 1,
                mb: 1,
                '&.Mui-selected': {
                  bgcolor: 'primary.light',
                  '&:hover': {
                    bgcolor: 'primary.light',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ color: currentPage === item.id ? 'white' : 'grey.600' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: currentPage === item.id ? 'bold' : 'normal',
                  color: currentPage === item.id ? 'white' : 'inherit',
                }}
              />
            </ListItemButton>
          ))}
        </List>

        <Box
          sx={{
            mt: 'auto',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={() => navigate('/docs')}
            startIcon={<DocsIcon />}
            sx={{ mb: 2 }}
          >
            API 문서
          </Button>

          <Button
            fullWidth
            variant="outlined"
            color="primary"
            onClick={() => navigate('/')}
            startIcon={<HomeIcon />}
            sx={{ mb: 2 }}
          >
            홈으로 돌아가기
          </Button>

          <Button
            fullWidth
            variant="outlined"
            color="error"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
          >
            로그아웃
          </Button>
        </Box>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: 'grey.50' }}>
        <Container maxWidth="lg">
          {renderPageContent()}
        </Container>
      </Box>
    </Box>
  );
};

export default ConsolePage;